import React, { useState } from "react";
import { BRAND } from "@/data/policies";
import { Mail, Phone, MessageCircle, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", order: "", subject: "ORDER", message: "" });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    // Placeholder: backend ticket endpoint will be wired in Phase 2 (SendGrid).
    // Form payload is intentionally not logged in the browser.
    toast.success("Got it — our team will reply within 24 hours.");
    setSent(true);
    setForm({ name: "", email: "", order: "", subject: "ORDER", message: "" });
  };

  return (
    <div className="bg-white" data-testid="contact-page">
      {/* Hero */}
      <section className="border-b border-black/10 py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs text-[#FF3B30] tracking-widest">SUPPORT</div>
          <h1 className="font-display text-6xl md:text-8xl tracking-tighter mt-2 text-black leading-[0.9]">
            HOW CAN<br />WE HELP?
          </h1>
          <p className="text-black/60 mt-4 max-w-2xl text-lg">
            Real humans, not bots. We answer every message within 24 hours — usually within 2.
            Got a quick one? Try our <a href="/faqs" className="underline text-black hover:text-[#FF3B30]">FAQs</a> first.
          </p>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-[1fr_1fr] gap-12">
        {/* Channels */}
        <div className="space-y-4">
          <h2 className="font-display text-3xl tracking-tighter text-black mb-6">REACH US</h2>

          <a href={BRAND.whatsappLink} target="_blank" rel="noreferrer" className="block border border-black/15 p-6 hover:border-[#FF3B30] hover:bg-black/[0.02] transition-colors" data-testid="contact-whatsapp">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#25D366] text-white flex items-center justify-center"><MessageCircle size={20} /></div>
              <div className="flex-1">
                <div className="font-display tracking-widest text-sm text-black">WHATSAPP — FASTEST</div>
                <div className="font-mono text-sm text-black/70 mt-1">{BRAND.whatsappDisplay}</div>
                <div className="text-xs text-black/50 mt-2">Typically replies in under 10 minutes during working hours.</div>
              </div>
            </div>
          </a>

          <a href={`mailto:${BRAND.email}`} className="block border border-black/15 p-6 hover:border-[#FF3B30] hover:bg-black/[0.02] transition-colors" data-testid="contact-email">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-black text-white flex items-center justify-center"><Mail size={20} /></div>
              <div className="flex-1">
                <div className="font-display tracking-widest text-sm text-black">EMAIL</div>
                <div className="font-mono text-sm text-black/70 mt-1">{BRAND.email}</div>
                <div className="text-xs text-black/50 mt-2">For order issues, defects, and detailed queries. Reply within 24 hours.</div>
              </div>
            </div>
          </a>

          <a href={`tel:${BRAND.phoneTel}`} className="block border border-black/15 p-6 hover:border-[#FF3B30] hover:bg-black/[0.02] transition-colors" data-testid="contact-phone">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-black text-white flex items-center justify-center"><Phone size={20} /></div>
              <div className="flex-1">
                <div className="font-display tracking-widest text-sm text-black">PHONE</div>
                <div className="font-mono text-sm text-black/70 mt-1">{BRAND.phoneDisplay}</div>
                <div className="text-xs text-black/50 mt-2">Mon to Sat, 9 AM – 9 PM IST.</div>
              </div>
            </div>
          </a>

          <div className="border border-black/15 p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-black/5 text-black flex items-center justify-center"><Clock size={20} /></div>
              <div className="flex-1">
                <div className="font-display tracking-widest text-sm text-black">SUPPORT HOURS</div>
                <div className="text-sm text-black/70 mt-1">{BRAND.hours}</div>
              </div>
            </div>
          </div>

          <div className="border border-black/15 p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-black/5 text-black flex items-center justify-center"><MapPin size={20} /></div>
              <div className="flex-1">
                <div className="font-display tracking-widest text-sm text-black">REGISTERED OFFICE</div>
                <div className="text-sm text-black/70 mt-1">{BRAND.address}</div>
                <div className="text-xs text-black/50 mt-2">No walk-ins / no warehouse pickups. Postal correspondence only.</div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div>
          <h2 className="font-display text-3xl tracking-tighter text-black mb-6">SUBMIT A TICKET</h2>
          {sent ? (
            <div className="border-2 border-[#FF3B30] p-8 text-center" data-testid="contact-success">
              <CheckCircle2 size={48} className="mx-auto text-[#FF3B30] mb-4" />
              <div className="font-display text-2xl tracking-tighter text-black">TICKET RECEIVED</div>
              <p className="text-black/60 mt-2">Our team will reply within 24 hours to your registered email.</p>
              <button onClick={() => setSent(false)} className="mt-6 underline font-display tracking-widest text-xs">SUBMIT ANOTHER</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4 border border-black/15 p-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="NAME" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="contact-name" />
                <Field label="EMAIL" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="contact-email-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="ORDER NUMBER (OPTIONAL)" value={form.order} onChange={(v) => setForm({ ...form, order: v })} placeholder="BE260612ABC123" testId="contact-order" />
                <label className="block">
                  <span className="font-display tracking-widest text-[10px] text-black/50">SUBJECT</span>
                  <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full bg-white border-b border-black/30 focus:border-black outline-none py-3 text-black mt-0" data-testid="contact-subject">
                    <option value="ORDER">Order issue</option>
                    <option value="RETURN">Return / Exchange</option>
                    <option value="DEFECT">Defective product</option>
                    <option value="PAYMENT">Payment / Refund</option>
                    <option value="SHIPPING">Shipping query</option>
                    <option value="WHOLESALE">Wholesale / Collab</option>
                    <option value="OTHER">Other</option>
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="font-display tracking-widest text-[10px] text-black/50">MESSAGE</span>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={6}
                  required
                  placeholder="Tell us what's up. The more detail, the faster we can help."
                  className="w-full bg-transparent border border-black/20 focus:border-black outline-none p-3 text-black placeholder:text-black/30 mt-1 text-sm"
                  data-testid="contact-message"
                />
              </label>
              <button
                type="submit"
                className="w-full bg-black text-white py-4 font-display tracking-widest text-sm hover:bg-[#FF3B30] transition-colors inline-flex items-center justify-center gap-2"
                data-testid="contact-submit"
              >
                <Send size={14} /> SUBMIT TICKET
              </button>
              <p className="text-xs text-black/40 font-mono">We never share your data. Read our <a href="/policies/privacy" className="underline">privacy policy</a>.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false, placeholder = "", testId }) {
  return (
    <label className="block">
      <span className="font-display tracking-widest text-[10px] text-black/50">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-black/30 focus:border-black outline-none py-3 text-black placeholder:text-black/30"
        data-testid={testId}
      />
    </label>
  );
}
